import React, { useState, useEffect } from 'react';
import { getPropertyById, getCalls } from '../../../services/mysteryShopService';
import type { MysteryShopProperty, MysteryShopCall } from '../../../types/mysteryShop';
import { StatCard, ScoreBadge, TourBadge } from '../shared';
import { getScoreColor, formatDateTime } from '../utils';

interface PropertyDetailProps {
  propertyId: string;
  onSelectCall: (id: string, agentName?: string, agentId?: string) => void;
  onSelectAgent: (agentId: string) => void;
}

export const PropertyDetail: React.FC<PropertyDetailProps> = ({ propertyId, onSelectCall, onSelectAgent }) => {
  const [property, setProperty] = useState<MysteryShopProperty | null>(null);
  const [calls, setCalls] = useState<MysteryShopCall[]>([]);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    Promise.all([
      getPropertyById(propertyId),
      getCalls({ propertyId })
    ]).then(([prop, propCalls]) => {
      setProperty(prop);
      setCalls(propCalls.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    });
  }, [propertyId, rev]);

  const avgScore = calls.length > 0
    ? calls.reduce((sum, c) => sum + c.overallScore, 0) / calls.length
    : 0;
  const tourRate = calls.length > 0
    ? (calls.filter(c => c.tourInvited).length / calls.length) * 100
    : 0;
  const agentList = Array.from(
    calls
      .filter(c => c.agentId && c.agentName)
      .reduce((map, c) => {
        if (!map.has(c.agentId!)) map.set(c.agentId!, c.agentName!);
        return map;
      }, new Map<string, string>())
  ).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{property?.name ?? '…'}</h1>
          <p className="text-gray-500 mt-1">{property?.address ?? ''}</p>
        </div>
        <button
          onClick={() => setRev(r => r + 1)}
          className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          title="Refresh"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 2v6h-6"/>
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
            <path d="M3 22v-6h6"/>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Average Score" value={avgScore.toFixed(1)} color={getScoreColor(avgScore)} />
        <StatCard label="Tour Invite Rate" value={`${tourRate.toFixed(0)}%`} />
        <StatCard label="Total Calls" value={calls.length} />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Call History</h2>
        </div>
        <div className="divide-y divide-gray-50">
          {calls.length > 0 ? (
            calls.map(call => (
              <div
                key={call.id}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onSelectCall(call.id, call.agentName || undefined, call.agentId || undefined)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className="font-medium text-gray-800">{call.agentName || 'Unknown Agent'}</p>
                      <TourBadge invited={call.tourInvited} />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{formatDateTime(call.date)}</p>
                  </div>
                  <ScoreBadge score={call.overallScore} size="md" />
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">No calls recorded for this property</div>
          )}
        </div>
      </div>

      {agentList.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Leasing Agents</h3>
          <div className="flex flex-wrap gap-2">
            {agentList.map(agent => (
              <button
                key={agent.id}
                onClick={() => onSelectAgent(agent.id)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                {agent.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
